import React from 'react';
import type { ISODateRange } from 'shared/src/date-string-utils/types';
import { useCubeQuery } from '@/components/display/use-cube-query';
import { useLineItemContext } from '@/features/line-items/line-items-context';
import { ActiveOverUnderMetric, ExpiredOverUnderMetric } from './over-under-metrics';
import { getDateRangeState } from './metric-date-utils';
import { MetricSkeleton } from '@/components/feedback/metric-skeleton';

type UnitCostOverUnderMetricProps = {
  dateRange: ISODateRange;
};

export function UnitCostOverUnderMetric({ dateRange }: UnitCostOverUnderMetricProps) {
  const { lineItem } = useLineItemContext();

  const result = useCubeQuery({
    dimensions: ['line_items.unit_price_type_name', 'line_item_metrics_daily.planned_unit_cost'],
    measures: [
      'line_item_metrics_daily.unit_cost_actual',
      'line_item_metrics_daily.unit_cost_variance'
    ],
    timeDimensions: [
      {
        dimension: 'line_item_metrics_daily.day',
        dateRange: [dateRange.from, dateRange.to]
      }
    ],
    filters: [
      {
        member: 'line_item_metrics_daily.line_item_id',
        operator: 'equals',
        values: [lineItem.id]
      }
    ]
  });

  const isExpired = getDateRangeState(dateRange) === 'expired';

  if (result.status === 'loading') return <MetricSkeleton />;
  if (result.status === 'error') return null;

  if (!result.rows[0] || !result.formattedRows[0]) {
    if (isExpired) {
      return (
        <ExpiredOverUnderMetric
          title="CPM"
          actualValue="$-.--"
          plannedValue="$-.--"
          variance={0}
          varianceFormatted=""
          favorableDirection="under"
        />
      );
    }
    return (
      <ActiveOverUnderMetric
        title="CPM"
        actualValue="$-.--"
        plannedValue="$-.--"
        variance={0}
        varianceFormatted=""
        favorableDirection="under"
      />
    );
  }

  const {
    'line_items.unit_price_type_name': unitPriceTypeName,
    'line_item_metrics_daily.unit_cost_variance': variance
  } = result.rows[0];
  const {
    'line_item_metrics_daily.unit_cost_actual': actualCost,
    'line_item_metrics_daily.planned_unit_cost': plannedCost,
    'line_item_metrics_daily.unit_cost_variance': varianceFormatted
  } = result.formattedRows[0];

  if (isExpired) {
    return (
      <ExpiredOverUnderMetric
        title={unitPriceTypeName}
        actualValue={actualCost}
        plannedValue={plannedCost}
        variance={variance}
        varianceFormatted={varianceFormatted}
        favorableDirection="under"
      />
    );
  }

  return (
    <ActiveOverUnderMetric
      title={unitPriceTypeName}
      actualValue={actualCost}
      plannedValue={plannedCost}
      variance={variance}
      varianceFormatted={varianceFormatted}
      favorableDirection="under"
    />
  );
}
